
import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { ThreeDots } from 'react-loader-spinner';
import '../css/skillsScreen.css';
import updateTokenUsage from './updateTokenUsage';
import { AI_FUNCTIONS_BASE_URL as FUNCTIONS_BASE_URL } from '../config/api';
import { buildTransferableSkillsLexicon } from './transferableSkillsLexicon';
import {
  buildSkillContextFromForm,
  optimizeSkillsForResume,
  randomizeSkills,
  sortSkillsByRelevance,
} from './skillCuration';

// --- helpers ---

/**
 * Remove HTML tags and excess whitespace from a string.  This helper
 * ensures that skills and signals are cleanly formatted before
 * comparison or display.
 *
 * @param {any} v The value to normalise.
 * @returns {string} A trimmed, plain text representation of ``v``.
 */
const clean = (v) =>
  String(v ?? '')
    .replace(/<[^>]*>/g, '')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Produce a case‑insensitive list of unique strings.  Existing order is
 * preserved and falsy values are ignored.
 *
 * @param {string[]} arr The array to deduplicate.
 * @returns {string[]} A new array containing each unique non‑empty entry.
 */
const uniqCI = (arr) => {
  const seen = new Set();
  const out = [];
  for (const s of arr || []) {
    const t = clean(s);
    const k = t.toLowerCase();
    if (!t || seen.has(k)) continue;
    seen.add(k);
    out.push(t);
  }
  return out;
};

/**
 * Collect salient context from the user's profile.  This replicates the
 * logic from the existing skills screen: majors and minors from
 * education history, course names or bullets, and job titles and
 * responsibilities from work history.  The collected values are
 * deduplicated and cleaned.
 *
 * @param {object} formData The form state from the parent component.
 * @returns {object} An object containing majors, minors, courses,
 *   workTitles and workBullets.
 */
/**
 * Test screen component for skill extraction using the o4‑mini model.
 *
 * The props mirror those expected by the existing SkillsScreen so that
 * the component can be dropped into the app with minimal changes.
 *
 * @param {object} props
 * @param {object} props.formData The shared form state.
 * @param {function} props.setFormData Setter to update the form state.
 * @param {function} props.setTokenUsage Setter for token usage (used by upstream logic).
 * @param {function} props.setUsagePercentage Setter for usage percentage (used by upstream logic).
 * @param {number} props.tokenThreshold The user's token limit (used by upstream logic).
 */
export default function O4SkillsScreen({
  formData,
  setFormData,
  setTokenUsage,
  setUsagePercentage,
  tokenThreshold,
}) {
  const [recommendations, setRecommendations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [err, setErr] = useState('');
  const [manualSkill, setManualSkill] = useState('');

  // Display context for the user
  const resumeLexicon = useMemo(
    () => buildTransferableSkillsLexicon(formData),
    [formData],
  );
  const skillContext = useMemo(
    () => buildSkillContextFromForm(formData, { signals: resumeLexicon.signals }),
    [formData, resumeLexicon.signals],
  );

  /**
   * Toggle a skill selection.  Clicking a recommended skill adds it to
   * the user's skills and selectedSkills lists; clicking it again
   * removes it.  Case‑insensitive matching is used to avoid
   * duplicates.
   *
   * @param {string} skill The skill to toggle.
   */
  const handleSelectSkill = (skill) => {
    const s = clean(skill);
    if (!s) return;
    const currentSkills = uniqCI(formData.skills || []);
    const currentSelected = uniqCI(formData.selectedSkills || []);
    const isSelected = currentSelected.map((x) => x.toLowerCase()).includes(s.toLowerCase());

    const nextSelected = isSelected
      ? currentSelected.filter((x) => x.toLowerCase() !== s.toLowerCase())
      : uniqCI([...currentSelected, s]);

    const nextSkills = isSelected
      ? currentSkills.filter((x) => x.toLowerCase() !== s.toLowerCase())
      : uniqCI([...currentSkills, s]);

    setFormData({ ...formData, selectedSkills: nextSelected, skills: nextSkills });
  };

  /**
   * Add a skill that the user typed manually.  The skill is cleaned and
   * added to both the skills and selectedSkills arrays.  Duplicate
   * entries are removed automatically.
   */
  const addManualSkill = () => {
    const s = clean(manualSkill);
    if (!s) return;
    const nextSkills = uniqCI([...(formData.skills || []), s]);
    const nextSelected = uniqCI([...(formData.selectedSkills || []), s]);
    setFormData({ ...formData, skills: nextSkills, selectedSkills: nextSelected });
    setManualSkill('');
  };

  /**
   * Select all recommended skills.  Merges the recommendations into the
   * current skills and selectedSkills lists.
   */
  const selectAll = () => {
    const chosen = uniqCI([...recommendations]);
    const nextSkills = uniqCI([...(formData.skills || []), ...chosen]);
    const nextSelected = uniqCI([...(formData.selectedSkills || []), ...chosen]);
    setFormData({ ...formData, skills: nextSkills, selectedSkills: nextSelected });
  };

  /**
   * Remove all recommended skills from the selection.  This does not
   * remove manually added skills.
   */
  const clearAllRecommendedFromSelection = () => {
    const recSet = new Set(recommendations.map((r) => r.toLowerCase()));
    const nextSelected = (formData.selectedSkills || []).filter((s) => !recSet.has(s.toLowerCase()));
    const nextSkills = (formData.skills || []).filter((s) => !recSet.has(s.toLowerCase()));
    setFormData({ ...formData, selectedSkills: nextSelected, skills: uniqCI(nextSkills) });
  };

  const optimizeCurrentSkills = () => {
    const sourceSkills = (formData.selectedSkills || []).length
      ? formData.selectedSkills
      : formData.skills;
    const optimized = optimizeSkillsForResume(sourceSkills, skillContext, formData);
    setFormData({ ...formData, skills: optimized, selectedSkills: optimized });
  };

  const randomizeCurrentSkills = () => {
    const sourceSkills = (formData.selectedSkills || []).length
      ? formData.selectedSkills
      : formData.skills;
    const shuffled = randomizeSkills(sourceSkills);
    setFormData({ ...formData, skills: shuffled, selectedSkills: shuffled });
  };

  /**
   * Fetch skill recommendations by invoking the backend API.  The
   * collected signals (courses, majors, minors and work titles) are
   * sent to the Azure Functions endpoint where the o4 model is
   * called.  On success the returned skills are cleaned and
   * de‑duplicated before updating the UI.  Token usage is refreshed
   * via ``updateTokenUsage`` if the necessary props are supplied.
   */
  const getRecommendations = async () => {
    setIsLoading(true);
    setErr('');
    try {
      // Assemble all available signals from the user's profile.  The backend
      // will deduplicate them again, but we perform basic cleaning here.
      const signals = resumeLexicon.signals;
      console.log('[O4SkillsScreen] Signals collected:', signals);

      if (!signals.length) {
        setErr('Add work, volunteer, military, training, certification, education, or other experience before requesting suggestions.');
        setRecommendations([]);
        return;
      }

      // Construct the request payload for the backend.  We use the
      // ``o4Skills`` prompt to trigger the new branch on the server.
      const payload = {
        prompt: 'o4Skills',
        userID: formData.user,
        signals,
        resumeLexicon,
      };

      const response = await axios.post(
        `${FUNCTIONS_BASE_URL}/api/jobSeriesSearchHttp`,
        payload,
        { headers: { 'Content-Type': 'application/json' } }
      );
      

      // The backend returns either a string, an array of skills or an
      // object containing a ``skills`` array.  Normalise these into a
      // list of cleaned suggestions and filter out any already
      // selected skills from the form data.
      const data = response.data;
      let suggested;
      if (Array.isArray(data)) {
        suggested = data;
      } else if (typeof data === 'string') {
        suggested = data.split(/[\n,]/).map((s) => clean(s));
      } else if (Array.isArray(data?.skills)) {
        suggested = data.skills.map((s) => clean(s));
      } else {
        suggested = [];
      }
      // Remove empty entries and already selected skills
      suggested = sortSkillsByRelevance(uniqCI(
        suggested
          .filter(Boolean)
          .filter((s) => !(formData.skills || []).some((e) => e.toLowerCase() === s.toLowerCase()))
      ), skillContext, formData);
      console.log('[O4SkillsScreen] Suggested skills from backend:', suggested);
      setRecommendations(suggested);

      // Optionally refresh token usage using the existing helper.  This
      // ensures the token meter stays up to date without reloading.
      if (formData.user && tokenThreshold && setTokenUsage && setUsagePercentage) {
        console.log('[O4SkillsScreen] Updating token usage for:', formData.user);
        try {
          await updateTokenUsage(formData.user, tokenThreshold, setTokenUsage, setUsagePercentage);
        } catch (e) {
          console.warn('[O4SkillsScreen] Token usage update failed:', e);
        }
      }
    } catch (e) {
      console.error('[O4SkillsScreen] Error fetching recommendations:', e);
      const message =
        e?.response?.data?.error?.message || e?.message || 'Failed to fetch recommendations. Please try again later.';
      setErr(message);
      setRecommendations([]);
    } finally {
      setIsLoading(false);
    }
  };


  // If we truly have no signals to display, short‑circuit the UI.  This
  // matches the behaviour of the existing SkillsScreen which asks
  // users to enter their academics first.  By returning early we
  // avoid rendering any of the controls.
  if (!resumeLexicon.hasExperienceSignals) {
    return (
      <p>
        Add work, volunteer, military, training, certification, education, or
        other experience to receive transferable skill suggestions.
      </p>
    );
  }

  // Render the UI.  We reuse the same CSS classes defined for the
  // original skills screen to maintain a consistent look and feel.  A
  // spinner is displayed while the model is generating suggestions.
  return (
    <div className="skills-screen">
      <h1>Skill Suggestions</h1>
      {!resumeLexicon.hasEducation && (
        <p className="skills-screen context-note">
          Suggestions will be based on your experience and credentials. College
          history is not required.
        </p>
      )}

      <div className="skills-screen responsive-box">
        {/* Academics / signals preview could be added here for display. */}

        {/* Action to fetch recommendations */}
        <button
          onClick={getRecommendations}
          className="skills-screen recommendation-button"
          disabled={isLoading}
          aria-busy={isLoading}
        >
          {isLoading ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <ThreeDots height="14" width="36" color="#ffffff" ariaLabel="loading" visible />
              Fetching skills…
            </span>
          ) : (
            'Get Skills Recommendations'
          )}
        </button>
        {err && <p className="skills-screen error-message">{err}</p>}

        {/* Recommendations as chips */}
        {recommendations.length > 0 && (
          <div className="recommendations">
            <div style={{ textAlign: 'center' }}>
              <h2 style={{ margin: 0 }}>Recommended Skills</h2>
            </div>

            <ul className="recommendations-list">
              {recommendations.map((skill, index) => {
                const isSelected =
                  (formData.selectedSkills || []).map((x) => x.toLowerCase()).includes(skill.toLowerCase());
                return (
                  <li key={index} className="recommendation-item">
                    <button
                      className={`recommendedItem ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleSelectSkill(skill)}
                      title={skill}
                    >
                      {skill}
                    </button>
                  </li>
                );
              })}
            </ul>

            <div className="skills-screen recommendations-actions">
              <button onClick={selectAll}>Select All</button>
              <button onClick={clearAllRecommendedFromSelection}>Clear</button>
            </div>
          </div>
        )}

        {/* Manual add */}
        <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
          <h3 style={{ margin: 0 }}>Add a Skill Manually</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8 }}>
            <input
              type="text"
              value={manualSkill}
              onChange={(e) => setManualSkill(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') addManualSkill();
              }}
              placeholder="e.g., Python, Classroom Management"
              style={{ width: '100%', border: '1px solid #ccc', borderRadius: 6, padding: '6px 8px' }}
            />
            <button onClick={addManualSkill}>Add</button>
          </div>
        </div>

        {/* Current snapshot */}
        <div style={{ marginTop: 12 }}>
          <h3 style={{ marginBottom: 6 }}>Your Current Skills (pipe‑separated)</h3>
          <div className="skills-screen recommendations-actions" style={{ marginBottom: 8 }}>
            <button type="button" onClick={optimizeCurrentSkills}>
              Optimize Skills
            </button>
            <button type="button" onClick={randomizeCurrentSkills}>
              Randomize Skills
            </button>
          </div>
          <div className="pipe-separated-list">
            {uniqCI(formData.skills || []).join(' | ') || '—'}
          </div>
        </div>
      </div>
    </div>
  );
}

